import { type Database } from "@fitness-app/supabase";

import { type ChallengeFeedMode } from "./ChallengeFeed";
import { type ClientRatingBreakdownKey } from "./ClientRating";

// TRE-1378 — Challenge (aggregator). Own points system; reuses the rating snapshot+realtime PATTERN only.

export type ChallengeTemplateEntity = Database["public"]["Tables"]["challenge_template"]["Row"];
export type ChallengeEntity = Database["public"]["Tables"]["challenge"]["Row"];
export type ChallengeTaskEntity = Database["public"]["Tables"]["challenge_task"]["Row"];
export type ChallengeParticipantEntity = Database["public"]["Tables"]["challenge_participant"]["Row"];
export type ChallengeTaskCompletionEntity = Database["public"]["Tables"]["challenge_task_completion"]["Row"];
export type ChallengeScoreSnapshotEntity = Database["public"]["Tables"]["challenge_score_snapshot"]["Row"];
export type ChallengeEventEntity = Database["public"]["Tables"]["challenge_event"]["Row"];

export enum ChallengeStatus {
  Upcoming = "upcoming",
  Active = "active",
  Finished = "finished",
  Archived = "archived",
}

export enum ChallengeTaskType {
  PlanActivity = "planActivity",
  ExtraTask = "extraTask",
  Habit = "habit",
  NumericGoal = "numericGoal",
  /** Video task backed by the existing exercise_recording mechanism (read-only scoring layer). */
  ExerciseRecording = "exerciseRecording",
}

export enum ChallengeVerification {
  Auto = "auto",
  SelfReport = "selfReport",
  ProofUpload = "proofUpload",
  TrainerApproval = "trainerApproval",
}

export enum ChallengeParticipantSource {
  Assigned = "assigned",
  SelfJoined = "self-joined",
}

export enum ChallengeParticipantStatus {
  Active = "active",
  Pending = "pending",
  Left = "left",
  Completed = "completed",
}

export enum ChallengeCompletionStatus {
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected",
  Auto = "auto",
}

/**
 * TRE-1576 — what "winning" means for a challenge:
 *  - leaderboard: classic ranking (default, today's behavior)
 *  - threshold: "everyone can be a winner" — reaching `passPoints` marks the participant Completed
 *  - both: pass mark + ranking coexist
 */
export enum ChallengeGoalType {
  Leaderboard = "leaderboard",
  Threshold = "threshold",
  Both = "both",
}

export enum ChallengeRecurrenceFrequency {
  None = "none",
  Weekly = "weekly",
  Monthly = "monthly",
}

/**
 * The category an auto/planActivity task maps to in ClientDayActivity — the rating keys
 * plus challenge-only signals (hydration comes from water-tracking events).
 */
export type ChallengeActivityCategory = ClientRatingBreakdownKey | "hydration";

export type ChallengeStreakBonusRule = {
  /** Award `bonus` points for every `everyDays` consecutive completion days. */
  everyDays: number;
  bonus: number;
};

export type ChallengeMilestone = {
  /** Cumulative value threshold (for numericGoal tasks). */
  value: number;
  bonus: number;
  label?: string;
};

export type ChallengeRecurrence = {
  frequency: ChallengeRecurrenceFrequency;
  /** Window length of each generated edition; falls back to template.defaultDurationDays. */
  durationDays?: number;
  autoAssignCohort?: boolean;
  cohortTraineeIds?: string[];
  /** ISO start date of the most recently generated edition (idempotency guard for the scheduler). */
  lastSpawnedStartDate?: string | null;
};

/**
 * Own scoring config for a challenge (analog of defaultClientRatingConfigData, but task-centric:
 * per-task weights live on ChallengeTask; this holds the global knobs).
 */
export type ChallengeScoringConfig = {
  /** Anti-farming: optional cap on total points awarded per participant per day. */
  dailyPointsCap?: number | null;
  /** Default streak rule applied to habit tasks that don't define their own. */
  defaultStreakBonus?: ChallengeStreakBonusRule | null;
  /** Override points-per-qualifying-day for planActivity tasks, keyed by activity category. */
  categoryWeights?: Partial<Record<ChallengeActivityCategory, number>>;
  version?: number;
};

/**
 * Persisted on an `ExerciseRecording` task so late joiners can have the backing extra-workout
 * re-applied (assignCohort / selfJoin). The challenge does not own the recording — it references it.
 */
export type ChallengeExerciseRecordingConfig = {
  /** The extra-workout template (with the flagged exercise) saved to each participant's calendar. */
  workoutTemplate?: Record<string, unknown>;
  /** Legacy single date (YYYY-MM-DD) — tasks created before multi-date support. */
  eventDate?: string;
  /** ISO dates (YYYY-MM-DD) the workout is scheduled for — one batch per date. */
  eventDates?: string[];
};

/** All scheduled dates of a recording task, normalizing the legacy single `eventDate` shape. */
export const getChallengeRecordingEventDates = (
  config: ChallengeExerciseRecordingConfig | null | undefined,
): string[] => {
  if (Array.isArray(config?.eventDates) && config.eventDates.length) {
    return config.eventDates;
  }
  return config?.eventDate ? [config.eventDate] : [];
};

/** Typed view over the `config` jsonb column on a challenge task. */
export type ChallengeTaskConfig = ChallengeExerciseRecordingConfig & {
  /**
   * Weekday schedule for date-driven tasks (extraTask / numericGoal / planActivity):
   * completions only count — and the UI only unlocks — on these days.
   * dayjs numbering (0 = Sunday … 6 = Saturday), same convention as habit schedules.
   * Absent/empty = task is active every day.
   */
  daysOfWeek?: number[];
  /**
   * planActivity: a day only qualifies once the category count reaches this threshold,
   * and the day is then scored binary (task.points once) instead of per-unit.
   * E.g. "drink 3 litres of water" = category hydration + perDayTarget 3000 (hydration counts ml).
   */
  perDayTarget?: number;
  /**
   * planActivity: score per WEEK instead of per day — a week (Mon–Sun) earns task.points
   * once it has at least this many qualifying days inside the challenge window.
   * E.g. "3 workouts a week" = category workouts + weeklyTarget 3;
   * "track meals all week" = category mealsTracked + perDayTarget N + weeklyTarget 7.
   */
  weeklyTarget?: number;
} & Record<string, unknown>;

/**
 * True when `date` (YYYY-MM-DD) falls on one of the task's scheduled days (no schedule = always).
 *
 * Two independent schedules, both optional and ANDed when set:
 *  - `eventDates` — an explicit date list. Today only the recording task form writes it, but the
 *    check lives here so a task configured with dates cannot nudge or score on an off day
 *    (TRE-1852: the reminder scheduler saw only `daysOfWeek` and treated a dated task as daily).
 *  - `daysOfWeek` — a weekday pattern.
 */
export const isChallengeTaskDayAllowed = (config: ChallengeTaskConfig | null | undefined, date: string): boolean => {
  const eventDates = getChallengeRecordingEventDates(config);
  if (eventDates.length && !eventDates.includes(date.slice(0, 10))) {
    return false;
  }

  const days = config?.daysOfWeek;
  if (!Array.isArray(days) || days.length === 0) {
    return true;
  }
  // Parse as local date; YYYY-MM-DD is TZ-stable for weekday derivation this way.
  const [year, month, day] = date.slice(0, 10).split("-").map(Number);
  const weekday = new Date(year!, (month ?? 1) - 1, day ?? 1).getDay();
  return days.includes(weekday);
};

export const defaultChallengeScoringConfig: ChallengeScoringConfig = {
  dailyPointsCap: null,
  defaultStreakBonus: { everyDays: 7, bonus: 50 },
  categoryWeights: {},
  version: 1,
};

export interface ChallengeTemplate extends Omit<
  ChallengeTemplateEntity,
  "scoringConfig" | "recurrence" | "goalType" | "feedMode"
> {
  scoringConfig: ChallengeScoringConfig;
  recurrence: ChallengeRecurrence | null;
  goalType: ChallengeGoalType;
  /** TRE-1655 — recurring editions inherit the feed mode from their template. */
  feedMode: ChallengeFeedMode;
}

/**
 * Audience segment for open self-join, stored on `challenge.audience` (null = whole org).
 * Same shape the admin targeting panel edits; enforced server-side by
 * `challenge_audience_matches()` inside `challenge_joinable_in_my_org()`.
 */
export type ChallengeAudienceSpec = {
  combinator: "AND" | "OR";
  productIds: string[];
  includeTags: string[];
  excludeTags: string[];
  statuses: string[];
  manualIds: string[];
  excludedIds: string[];
};

/** Who sees what on the leaderboard — enforced server-side in the challenge_leaderboard RPC. */
export enum ChallengeLeaderboardVisibility {
  /** Everyone sees the whole ranking. */
  Full = "full",
  /** Only the top 5 rows + the caller's own row. */
  Top5PlusYou = "top5PlusYou",
  /** Full ranking, but names are hidden (null) for everyone except the caller. */
  Anonymized = "anonymized",
}

export interface Challenge extends Omit<
  ChallengeEntity,
  "scoringConfig" | "status" | "leaderboardVisibility" | "audience" | "goalType" | "feedMode"
> {
  scoringConfig: ChallengeScoringConfig;
  status: ChallengeStatus;
  leaderboardVisibility: ChallengeLeaderboardVisibility;
  audience: ChallengeAudienceSpec | null;
  goalType: ChallengeGoalType;
  /** TRE-1655 — feed mode: none | readonly | comments | full. */
  feedMode: ChallengeFeedMode;
}

/**
 * Focal point of the cover (0–100 per axis), CSS `object-position` semantics: 0 anchors the left/top
 * edge, 50 centres, 100 anchors the right/bottom.
 *
 * The cover is stored as 16:9, but no surface renders it at 16:9: the list cards are much WIDER
 * (~2.7–2.8:1), so `object-fit: cover` there scales to the width and cuts the top and bottom — heads
 * first. The hero, whose height follows its content, can end up narrower than 16:9 and then cuts the
 * sides. Hence both axes.
 */
export const DEFAULT_CHALLENGE_COVER_POSITION = 50;

/** Clamps a stored/edited focal point into the range the DB constraint accepts. */
export const clampCoverPosition = (value: number | null | undefined): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return DEFAULT_CHALLENGE_COVER_POSITION;
  }
  return Math.min(100, Math.max(0, Math.round(value)));
};

/** Ready-to-use CSS `object-position` / `background-position` value for a cover. */
export const coverPositionStyle = (x: number | null | undefined, y: number | null | undefined): string =>
  `${clampCoverPosition(x)}% ${clampCoverPosition(y)}%`;

/** True when the challenge has a working pass mark (threshold/both + passPoints set). */
export const challengeHasPassMark = (
  challenge: Pick<Challenge, "goalType" | "passPoints">,
): challenge is Pick<Challenge, "goalType" | "passPoints"> & { passPoints: number } =>
  challenge.passPoints != null &&
  challenge.passPoints > 0 &&
  (challenge.goalType === ChallengeGoalType.Threshold || challenge.goalType === ChallengeGoalType.Both);

export interface ChallengeTask extends Omit<
  ChallengeTaskEntity,
  "type" | "verification" | "activityCategory" | "milestoneThresholds" | "streakBonus" | "config"
> {
  type: ChallengeTaskType;
  verification: ChallengeVerification;
  activityCategory: ChallengeActivityCategory | null;
  milestoneThresholds: ChallengeMilestone[] | null;
  streakBonus: ChallengeStreakBonusRule | null;
  /** Target exercise for `exerciseRecording` tasks; scoring matches recordings by this id. */
  parentExerciseId: string | null;
  config: ChallengeTaskConfig | null;
}

export interface ChallengeParticipant extends Omit<ChallengeParticipantEntity, "source" | "status"> {
  source: ChallengeParticipantSource;
  status: ChallengeParticipantStatus;
}

export interface ChallengeTaskCompletion extends Omit<ChallengeTaskCompletionEntity, "status"> {
  status: ChallengeCompletionStatus;
}

/** Per-task and per-type point contributions for a participant. */
export type ChallengeScoreBreakdown = {
  byTask: Record<string, number>;
  byType: Partial<Record<ChallengeTaskType, number>>;
};

export interface ChallengeScoreSnapshot extends Omit<ChallengeScoreSnapshotEntity, "breakdown"> {
  breakdown: ChallengeScoreBreakdown;
}

export interface ChallengeWithTasks extends Challenge {
  tasks: ChallengeTask[];
}

// ---------------------------------------------------------------------------
// TRE-1654 — event calendar (dated events with a link, e.g. Saturday live workouts)
// ---------------------------------------------------------------------------

/** Where an event sits relative to now — drives the agenda grouping and the "trwa teraz" badge. */
export enum ChallengeEventState {
  Live = "live",
  Upcoming = "upcoming",
  Past = "past",
}

/** Assumed length of an event without `endAt`, used only to decide how long it counts as live. */
export const CHALLENGE_EVENT_DEFAULT_DURATION_MINUTES = 60;

/**
 * Guard for links that are opened straight from the app. Mirrors the DB constraint
 * `challenge_event_link_url_scheme` so a row written before that constraint existed —
 * or through the service role — can never hand a `javascript:` URL to `Linking.openURL`.
 */
export const isSafeChallengeEventLink = (url: string | null | undefined): url is string =>
  typeof url === "string" && /^https?:\/\//i.test(url.trim());

export const getChallengeEventState = (
  event: Pick<ChallengeEventEntity, "startAt" | "endAt">,
  now: number = Date.now(),
): ChallengeEventState => {
  const start = new Date(event.startAt).getTime();
  const end = event.endAt ? new Date(event.endAt).getTime() : start + CHALLENGE_EVENT_DEFAULT_DURATION_MINUTES * 60_000;

  if (now < start) {
    return ChallengeEventState.Upcoming;
  }
  return now <= end ? ChallengeEventState.Live : ChallengeEventState.Past;
};

export const defaultChallengeScoreBreakdown: ChallengeScoreBreakdown = {
  byTask: {},
  byType: {},
};
